// Pure helpers for deciding what a participant video tile renders. Extracted
// from VoiceChannelView so the freeze-vs-avatar rule is unit-testable (same
// pattern as getTileLayout / videoRect).

/** A track we only need the `readyState` of — lets tests pass plain objects. */
type ReadyStateLike = Pick<MediaStreamTrack, 'readyState'>

/**
 * Whether a tile has a *renderable* video track — i.e. one we should keep
 * painting (holding the last frame) rather than dropping to the avatar.
 *
 * IMPORTANT — do NOT re-add a `!t.muted` filter here.
 *
 * A frozen INCOMING video track fires `muted` (no media arriving) even though
 * the peer's camera is still published and ON. Electron's media stack flips the
 * track muted on the receive-side decode stalls a busy call produces; a plain
 * browser does NOT mark the track muted on the same stalls, so its `<video>`
 * just holds the last frame and resumes. Prod telemetry (voice_diag, 2026-06-23
 * team call): incoming video sat at 0 fps ~30% of the time for Electron
 * receivers vs ~16% for browser receivers — and only Electron flipped the track
 * muted, which is why the "мерцание камеры со спинером" was desktop-only.
 *
 * Filtering muted-but-not-ended tracks out is what dropped the tile from the
 * held last frame to the avatar on every transient freeze (and back) — read as
 * a spinner/flicker that grabbed attention. The 2026-06-19 fix kept `videoReady`
 * through the `<video>` waiting/stalled events but missed this path
 * (videoActive → shouldShowVideo), so the avatar still flashed. We now hold the
 * last frame and only treat a track as gone once it has actually ENDED.
 *
 * A camera that is genuinely OFF / bandwidth-suppressed still drops to the
 * avatar via the authoritative `participant.isVideoOn` (LiveKit isCameraEnabled)
 * gate in shouldShowVideo — that flips false on unpublish; a transient freeze
 * does not.
 */
export function hasRenderableVideo(videoTracks: ReadyStateLike[]): boolean {
  return videoTracks.some((t) => t.readyState !== 'ended')
}

/** Minimal shape of a LiveKit LocalVideoTrack for the self-preview gate — only
 *  the live `mediaStreamTrack` getter matters here. */
type LocalVideoTrackLike = { mediaStreamTrack?: ReadyStateLike | null }

/**
 * Whether the LOCAL self-view should paint its own camera.
 *
 * Unlike remote tiles (which snapshot pub.track.mediaStreamTrack into a
 * MediaStream and rely on hasRenderableVideo to hold the last frame through
 * transient freezes), the self preview is bound to the LiveKit LocalVideoTrack
 * via `track.attach(<video>)`. LiveKit re-attaches the element to the live
 * (processor-swapped) track on every replaceTrack / setProcessor / stopProcessor
 * / restart, so the element never strands on a dead track. We therefore only
 * need two things to be true to show video: the camera is ON
 * (LiveKit isCameraEnabled) and the current local track is not ended.
 *
 * OSPROD-208: the previous path snapshotted pub.track.mediaStreamTrack once;
 * an in-place camera processor swap (background blur / virtual background →
 * setProcessor/replaceTrack) ended that cached track WITHOUT firing a
 * LocalTrackPublished/Unpublished event, so the tile never rebuilt and
 * self-view fell back to the avatar while peers still received live video.
 * Gating on the live getter (not the cached snapshot) + the attach() binding
 * fixes it. See VOICE_CRITICAL_INVARIANTS #1/#2 and services/CLAUDE.md.
 */
export function isSelfCameraRenderable(
  track: LocalVideoTrackLike | null | undefined,
  cameraEnabled: boolean,
): boolean {
  if (!cameraEnabled || !track) return false
  // mediaStreamTrack getter returns processor?.processedTrack ?? _mediaStreamTrack
  // (always the CURRENT live track when read fresh). Only a genuinely ENDED
  // track means "no video" — a momentarily-undefined getter during a swap still
  // holds the last frame, matching hasRenderableVideo's non-flicker contract.
  return track.mediaStreamTrack?.readyState !== 'ended'
}

/** Minimal shape of the bits we touch on the self-preview <video> element. */
type SelfPreviewElement = { srcObject: unknown; muted: boolean }
/** Minimal shape of a LiveKit LocalVideoTrack's attach(). */
type AttachableTrack = { attach: (el: unknown) => void }

/**
 * Attach the self-preview CAMERA track to its `<video>` element WITHOUT turning
 * on self-monitoring (the user hearing their own mic).
 *
 * OSPROD-208 / 1.1.856A self-monitoring regression: LiveKit's `attachToElement`
 * REUSES the element's existing `srcObject` MediaStream and then sets
 * `element.muted = mediaStream.getAudioTracks().length === 0`. The snapshot path
 * leaves the local MIC track in that `srcObject` (`getParticipantStream` returns
 * `[mic, camera]`), so attaching the camera leaves an audio track present →
 * `muted=false` → the user hears their own mic the instant the camera turns on
 * (peers hear them fine). We therefore:
 *   1. clear `srcObject` first, so `attach()` builds a fresh CAMERA-ONLY stream;
 *   2. hard-assert `muted=true` after — the self preview must NEVER play audio
 *      (all voice routes through LiveKitBackgroundAudio).
 * See VOICE_CRITICAL_INVARIANTS #69.
 */
export function attachSelfPreview(el: SelfPreviewElement, track: AttachableTrack): void {
  el.srcObject = null
  try { track.attach(el) } catch { /* noop — attach is best-effort */ }
  el.muted = true
}
